import { FolderPlus, Search } from "lucide-react";
import { FC } from "react";
import { FormattedMessage } from "react-intl";
import { Folder } from "~/api/model";
import { Loader } from "~/components/Loader";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { CreateCaseDialog } from "../CreateCaseDialog";

interface FtueCreateCaseProps {
  firstName: string;
  demoCase?: Folder;
  isDemoCaseLoading?: boolean;
  isCompletingFtue: boolean;
  onCreateCaseClick: () => void;
  onCaseCreated: () => void;
  onExploreDemoCase: () => void;
  onSkip: () => void;
}

export const FtueCreateCase: FC<FtueCreateCaseProps> = ({
  firstName,
  demoCase,
  isDemoCaseLoading,
  isCompletingFtue,
  onCreateCaseClick,
  onCaseCreated,
  onExploreDemoCase,
  onSkip,
}) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-4">
      <div className="max-w-2xl w-full text-center space-y-8">
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold text-foreground">
            <FormattedMessage
              id="ftue.welcome.title"
              defaultMessage="Welcome to Sky AI, {firstName}!"
              values={{ firstName }}
            />
          </h1>
          <p className="text-lg text-muted-foreground">
            <FormattedMessage
              id="ftue.welcome.subtitle"
              defaultMessage="Upload your documents for any matter. Get organized files, summaries, and a polished report — in minutes."
            />
          </p>
        </div>

        <div className="flex items-center justify-center gap-4 md:flex-row flex-col">
          <CreateCaseDialog
            isFirstCase={true}
            onCaseCreated={onCaseCreated}
            TriggerButton={
              <Card
                className="md:w-[50%] w-full cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                onClick={onCreateCaseClick}
              >
                <CardHeader className="text-center">
                  <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <FolderPlus className="w-6 h-6 text-blue-600" />
                  </div>
                  <CardTitle className="text-lg">
                    <FormattedMessage
                      id="ftue.welcome.createCase"
                      defaultMessage="Create Your First Case"
                    />
                  </CardTitle>
                  <CardDescription className="mt-2">
                    <FormattedMessage
                      id="ftue.welcome.createCaseDescription"
                      defaultMessage="Upload your documents and get them organized, summarized, and ready for reporting."
                    />
                  </CardDescription>
                </CardHeader>
              </Card>
            }
          />

          {demoCase ? (
            <Card
              className="md:w-[50%] w-full cursor-pointer hover:border-purple-500 hover:shadow-md transition-all"
              onClick={onExploreDemoCase}
            >
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <Search className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">
                  <FormattedMessage
                    id="ftue.welcome.exploreDemo"
                    defaultMessage="Explore Demo Case"
                  />
                </CardTitle>
                <CardDescription>
                  <FormattedMessage
                    id="ftue.welcome.exploreDemoDescription"
                    defaultMessage="See how your documents get organized, summarized, and turned into reports."
                  />
                </CardDescription>
              </CardHeader>
            </Card>
          ) : isDemoCaseLoading ? (
            <Card className="md:w-[50%] w-full border-dashed border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/50">
              <CardHeader className="text-center">
                <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                  <Loader size={24} color="#9333ea" thickness={2} />
                </div>
                <CardTitle className="text-lg text-purple-700 dark:text-purple-300">
                  <FormattedMessage
                    id="ftue.welcome.demoCaseLoading"
                    defaultMessage="Setting up your demo case..."
                  />
                </CardTitle>
                <CardDescription>
                  <FormattedMessage
                    id="ftue.welcome.demoCaseLoadingDescription"
                    defaultMessage="This will only take a moment."
                  />
                </CardDescription>
              </CardHeader>
            </Card>
          ) : null}
        </div>

        {demoCase && (
          <div>
            <Button
              variant="link"
              onClick={onSkip}
              disabled={isCompletingFtue}
              className="text-muted-foreground hover:text-foreground"
            >
              <FormattedMessage
                id="ftue.welcome.skip"
                defaultMessage="Skip to Dashboard"
              />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
